// HM-317.r cluster 15.4: hero CTA "Assegna Camera" SPEC mockup v3.
// Banner gradient #FCF1EA -> #FAF7F2 con info dinamica sulle camere
// libere e bottone CTA arancio. Click -> chiama l'algoritmo piano-first
// del backend (HM-318.r) e apre il wizard walk-in con la camera scelta
// pre-popolata.

import { toast } from '@hotel-manager/ui/components';
import { Plus } from '@hotel-manager/ui/icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError } from '../../api/client.js';
import { type RoomDb, fetchNextAvailableRoom } from '../../api/rooms.js';
import { useWalkInWizard } from '../../stores/walkIn.js';

// HM-FIX-16.3 BUG3: importato dal helper centralizzato
import { buildingLabelOf } from '../../lib/buildingLabel.js';

export function HeroAssignRoom() {
  const queryClient = useQueryClient();
  const openWalkIn = useWalkInWizard((s) => s.open);

  // Polling next-available ogni 30s (refresh fast quando arriva nuova WS event
  // tramite invalidazione esterna in useDashboardLiveSync).
  const nextQuery = useQuery({
    queryKey: ['rooms', 'next-available'],
    queryFn: async () => {
      try {
        return await fetchNextAvailableRoom();
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) {
          return null; // stato "no_rooms_available" = NULL invece che throw
        }
        throw err;
      }
    },
    staleTime: 30_000,
    retry: (failureCount, err) => {
      if (err instanceof ApiError && err.status === 401) return false;
      return failureCount < 1;
    },
  });

  const next = nextQuery.data;
  // QA sweep 2026-07-22 (bug BASSO): 'camera'+'e' produceva "camerae".
  const subtitle = next
    ? `${next.totalAvailable} ${
        next.totalAvailable === 1 ? 'camera libera' : 'camere libere'
      } · prossima: ${next.roomCode} (${buildingLabelOf(next.building)})`
    : nextQuery.isLoading
      ? 'Verifica disponibilita…'
      : 'Nessuna camera libera al momento';

  const onAssign = async () => {
    if (!next) {
      toast.error('Nessuna camera libera disponibile');
      return;
    }
    // Recupero RoomDb completa dalla cache rooms gia' fetchata dalla
    // Dashboard. Se non in cache, refetch on-demand via getQueryData
    // semplice: rooms query e' parte del Dashboard mount.
    const cached = queryClient.getQueryData<{ rooms: RoomDb[] }>(['rooms']);
    const room = cached?.rooms.find((r) => r.id === next.roomId);
    if (!room) {
      toast.error('Camera non disponibile in cache, ricarica la pagina');
      return;
    }
    openWalkIn(room);
  };

  return (
    <article
      data-testid="hero-assign-room"
      className="rounded-2xl border p-6 mb-5 flex items-center justify-between gap-4 flex-wrap"
      style={{
        background: 'linear-gradient(135deg, #FCF1EA 0%, #FAF7F2 100%)',
        borderColor: '#F5C7A7',
      }}
    >
      <div className="flex flex-col gap-1 min-w-0">
        <div className="text-xl font-bold text-text-primary tracking-[-0.02em]">
          Cliente in arrivo?
        </div>
        <div className="text-[13px] text-text-secondary">
          Assegna automaticamente la prima camera libera disponibile
        </div>
        <div className="text-xs font-medium text-accent-primary-text mt-1">{subtitle}</div>
      </div>
      <button
        type="button"
        data-testid="btn-assign-room"
        onClick={onAssign}
        disabled={!next}
        className="flex items-center gap-2 px-7 py-3.5 rounded-[10px] bg-accent-primary text-text-on-accent text-[15px] font-semibold cursor-pointer transition-all duration-150 hover:bg-accent-primary-hover hover:-translate-y-px disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:translate-y-0"
        style={{
          boxShadow: '0 4px 12px rgba(233, 123, 61, 0.25)',
        }}
      >
        <Plus size={20} strokeWidth={2.25} />
        Assegna Camera
      </button>
    </article>
  );
}
